import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, MenuItem, TextField, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

export interface EditStudentTarget {
  user_id:    string
  email:      string
  first_name: string
  last_name:  string
}

interface ProfileFields {
  phone:         string | null
  grade:         number | null
  school_name:   string | null
  school_city:   string | null
  school_state:  string | null
  date_of_birth: string | null
}

interface Props {
  open:    boolean
  target:  EditStudentTarget | null
  onClose: () => void
  onSaved: () => void
}

const GRADES = [4, 5, 6, 7, 8, 9, 10, 11, 12]

export default function EditStudentDialog({ open, target, onClose, onSaved }: Props) {
  const [firstName, setFirstName] = useState('')
  const [lastName,  setLastName]  = useState('')
  const [email,     setEmail]     = useState('')
  const [phone,     setPhone]     = useState('')
  const [grade,     setGrade]     = useState<string>('')
  const [school,    setSchool]    = useState('')
  const [city,      setCity]      = useState('')
  const [state,     setState]     = useState('')
  const [dob,       setDob]       = useState('')
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  // Parent / guardian contact (a linked users row, role=parent)
  const [parentFirst, setParentFirst] = useState('')
  const [parentLast,  setParentLast]  = useState('')
  const [parentEmail, setParentEmail] = useState('')
  const [parentPhone, setParentPhone] = useState('')
  const [parentLinked, setParentLinked] = useState(false)
  const [inviting,    setInviting]    = useState(false)
  const [parentMsg,   setParentMsg]   = useState<string | null>(null)

  useEffect(() => {
    if (!open || !target) return
    setFirstName(target.first_name ?? '')
    setLastName(target.last_name ?? '')
    setEmail(target.email ?? '')
    setPhone(''); setGrade(''); setSchool(''); setCity(''); setState(''); setDob('')
    setParentFirst(''); setParentLast(''); setParentEmail(''); setParentPhone('')
    setParentLinked(false); setParentMsg(null)
    setError(null)

    let cancelled = false
    async function load() {
      if (!target) return
      setLoadingProfile(true)

      const { data: spData } = await supabase
        .from('student_profile')
        .select('student_id, grade, school_name, school_city, school_state, date_of_birth')
        .eq('user_id', target.user_id)
        .maybeSingle()
      const sp = spData as (ProfileFields & { student_id?: string }) | null
      if (!cancelled && sp) {
        setGrade(sp.grade != null ? String(sp.grade) : '')
        setSchool(sp.school_name ?? '')
        setCity(sp.school_city ?? '')
        setState(sp.school_state ?? '')
        setDob(sp.date_of_birth ?? '')
      }

      const { data: u } = await supabase
        .from('users').select('phone').eq('user_id', target.user_id).maybeSingle()
      if (!cancelled) setPhone((u as { phone?: string | null } | null)?.phone ?? '')

      // Parent contact: the linked parent account, else fall back to the
      // trial-signup contact so staff can invite from what we already have.
      let foundParent = false
      if (sp?.student_id) {
        const { data: link } = await supabase
          .from('parent_student_link')
          .select('parent_user_id')
          .eq('student_id', sp.student_id)
          .limit(1)
          .maybeSingle()
        if (link?.parent_user_id) {
          const { data: pu } = await supabase
            .from('users')
            .select('first_name, last_name, email, phone')
            .eq('user_id', link.parent_user_id)
            .maybeSingle()
          if (!cancelled && pu) {
            setParentFirst(pu.first_name ?? '')
            setParentLast(pu.last_name ?? '')
            setParentEmail(pu.email ?? '')
            setParentPhone((pu as { phone?: string | null }).phone ?? '')
            setParentLinked(true)
            foundParent = true
          }
        }
      }
      if (!foundParent && target.email) {
        const { data: lead } = await supabase
          .from('trial_lead')
          .select('parent_email, parent_phone')
          .eq('student_email', target.email.toLowerCase())
          .maybeSingle()
        if (!cancelled && lead) {
          setParentEmail(lead.parent_email ?? '')
          setParentPhone(lead.parent_phone ?? '')
        }
      }

      if (!cancelled) setLoadingProfile(false)
    }
    load()
    return () => { cancelled = true }
  }, [open, target])

  async function handleSave() {
    if (!target) return
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('Name and email are required.'); return
    }
    setSaving(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('update-student', {
      body: {
        user_id:       target.user_id,
        first_name:    firstName.trim(),
        last_name:     lastName.trim(),
        email:         email.trim(),
        phone:         phone.trim() || null,
        grade:         grade ? Number(grade) : null,
        school_name:   school.trim(),
        school_city:   city.trim(),
        school_state:  state.trim(),
        date_of_birth: dob || null,
      },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setSaving(false); return }
    if (data?.error) { setError(data.error); setSaving(false); return }

    // Persist parent/guardian contact (create or update the linked parent).
    if (parentEmail.trim()) {
      const err = await saveParentContact()
      if (err) { setError(err); setSaving(false); return }
    }

    setSaving(false)
    onSaved()
    onClose()
  }

  // Upsert the parent contact via edge function. Returns an error string or null.
  async function saveParentContact(): Promise<string | null> {
    if (!target) return null
    const { data, error: invokeErr } = await supabase.functions.invoke('set-parent-contact', {
      body: {
        student_user_id:   target.user_id,
        parent_email:      parentEmail.trim(),
        parent_phone:      parentPhone.trim() || null,
        parent_first_name: parentFirst.trim() || null,
        parent_last_name:  parentLast.trim() || null,
      },
    })
    if (invokeErr) return await readFunctionError(invokeErr)
    if (data?.error) return data.error
    setParentLinked(true)
    return null
  }

  async function handleInvite() {
    if (!target) return
    if (!parentEmail.trim()) { setParentMsg('Enter a parent email first.'); return }
    setInviting(true)
    setParentMsg(null)
    setError(null)

    // Make sure the latest contact is saved before inviting.
    const saveErr = await saveParentContact()
    if (saveErr) { setError(saveErr); setInviting(false); return }

    const { data, error: invokeErr } = await supabase.functions.invoke('invite-parent', {
      body: { student_user_id: target.user_id },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setInviting(false); return }
    if (data?.error) { setError(data.error); setInviting(false); return }

    setParentMsg(`Invite sent to ${parentEmail.trim()}.`)
    setInviting(false)
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit Student</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loadingProfile ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="First name" size="small" fullWidth required
                value={firstName} onChange={e => setFirstName(e.target.value)} />
              <TextField label="Last name" size="small" fullWidth required
                value={lastName} onChange={e => setLastName(e.target.value)} />
            </Box>
            <TextField label="Email" size="small" type="email" fullWidth required
              value={email} onChange={e => setEmail(e.target.value)}
              helperText="Email change takes effect immediately." />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Phone (optional)" size="small" type="tel" fullWidth
                value={phone} onChange={e => setPhone(e.target.value)} />
              <TextField label="Date of birth (optional)" size="small" type="date" fullWidth
                value={dob} onChange={e => setDob(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Grade" select size="small" sx={{ width: 130 }}
                value={grade} onChange={e => setGrade(e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {GRADES.map(g => <MenuItem key={g} value={String(g)}>{g}</MenuItem>)}
              </TextField>
              <TextField label="School" size="small" fullWidth
                value={school} onChange={e => setSchool(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="School city" size="small" fullWidth
                value={city} onChange={e => setCity(e.target.value)} />
              <TextField label="School state" size="small" sx={{ width: 130 }}
                value={state} onChange={e => setState(e.target.value)} />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                Parent / Guardian
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {parentLinked
                  ? 'Editing the linked parent account. Changing the email updates their sign-in.'
                  : 'No parent account linked yet — saving will create one.'}
              </Typography>
            </Box>
            {parentMsg && <Alert severity="success" sx={{ mt: 0.5 }}>{parentMsg}</Alert>}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Parent first name" size="small" fullWidth
                value={parentFirst} onChange={e => setParentFirst(e.target.value)} />
              <TextField label="Parent last name" size="small" fullWidth
                value={parentLast} onChange={e => setParentLast(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Parent email" size="small" type="email" fullWidth
                value={parentEmail} onChange={e => setParentEmail(e.target.value)} />
              <TextField label="Parent phone" size="small" type="tel" fullWidth
                value={parentPhone} onChange={e => setParentPhone(e.target.value)} />
            </Box>
            <Box>
              <Button
                variant="outlined" size="small"
                onClick={handleInvite}
                disabled={inviting || saving || !parentEmail.trim()}
              >
                {inviting ? <CircularProgress size={18} /> : 'Send parent invite'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Emails the parent a link to set their password and open the portal.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loadingProfile}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
